import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

import { ReactionListItem } from "./ReactionListItem";

type ReactionListProps = {
  messageId: string;
};

export function ReactionList({ messageId }: ReactionListProps): React.ReactNode {
  const { t } = useTranslation();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: reactionsData,
    fetchNextPage: fetchMoreReactions,
    hasNextPage: hasMoreReactions,
  } = useInfiniteQuery({
    ...communityFeedQueries.reactionsInfinite(messageId),
  });

  const allReactions = useMemo(() => {
    return reactionsData?.pages.flatMap((x) => x.items ?? []) ?? [];
  }, [reactionsData?.pages]);

  return (
    <Stack gap="md" data-testid="like-list">
      <Stack gap="md" as="ul" data-testid="reactions-section">
        {allReactions.map((reaction) => (
          <ReactionListItem key={reaction.author.id} {...{ reaction }} />
        ))}
      </Stack>
      {hasMoreReactions && (
        <Button variant="ghostPrimary" onClick={() => fetchMoreReactions()}>
          <span className="text-caption">{t("component.community-post.likes.see-more")}</span>
        </Button>
      )}
    </Stack>
  );
}
